import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import { isVerifyingCreditCard } from '../../reducers/checkoutPaymentReducer';
import {
    creditCardFormUpdated,
    creditCardVaultedError,
    toggleBillingAddress,
} from '../../actions/checkoutPaymentActions';
import { setTriggeredErrors } from '../../actions/checkoutUIActions';
import { creditCardFields, verifyCardFields } from '../../helpers/checkoutForms';
import { trackFormAction, trackFormError } from '../../helpers/checkoutTracking';
import { STEPS } from '../../constants/checkoutConstants';
import ctaStyles from '../../components/shared/styles/checkoutCTAButton.scss';
import styles from './styles/checkoutCreditCardForm.scss';

import { Checkbox } from 'dibs-elements/exports/Checkbox';
import CreditCardForm from 'dibs-credit-card-form';
import { ControlledAddressForm } from 'dibs-address-form';
import CheckoutContinueAction from '../../components/shared/actions/CheckoutContinueAction';
import CheckoutSaveAndContinueAction from '../../components/shared/actions/CheckoutSaveAndContinueAction';
import CheckoutEoriVatForm from '../../components/forms/CheckoutEoriVatForm';

class CheckoutCreditCardForm extends Component {
    constructor(props) {
        super(props);
        this.onToggle = this.onToggle.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.state = {
            errors: null,
        };
    }
    componentDidMount() {
        this.mounted = true;
    }
    componentDidUpdate(prevProps) {
        if (prevProps.checkoutPaymentErrors !== this.props.checkoutPaymentErrors) {
            this.setState({ errors: this.props.checkoutPaymentErrors });
        }
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    getSelectedCreditCard() {
        const { buyer, creditCardToken } = this.props;
        const creditCard = (buyer?.wallet?.paymentMethods || []).find(
            ({ paymentMethod }) => paymentMethod.token === creditCardToken
        );
        return creditCard?.paymentMethod;
    }
    onToggle(checked) {
        const {
            onToggleBillingAddress,
            isBillingSameAsShipping,
            onValidationError,
            checkoutPaymentErrors,
        } = this.props;
        onToggleBillingAddress(checked);
        const status = isBillingSameAsShipping ? 'selected' : 'deselected';
        trackFormAction(`billing address same as shipping address ${status}`);
        if (!isBillingSameAsShipping) {
            const creditCardValidation = creditCardFields.reduce(
                (acc, cur) =>
                    checkoutPaymentErrors[cur]
                        ? {
                              isValid: false,
                              errors: { ...acc.errors, [cur]: checkoutPaymentErrors[cur] },
                          }
                        : acc,
                { isValid: true, errors: {} }
            );
            onValidationError({
                isValid: creditCardValidation.isValid,
                errors: creditCardValidation.errors,
            });
        }
    }
    onChange({ field, value }) {
        const { isVerifying, onCreditCardFormUpdated } = this.props;
        onCreditCardFormUpdated(field, value, isVerifying);
    }
    onBlur({ field, error }) {
        const { onValidationUpdate, onTriggeredErrors } = this.props;
        const { errors } = this.state;
        if (error) {
            trackFormError({ step: STEPS.STEP_PAYMENT, field, error });
            onTriggeredErrors([field]);
            onValidationUpdate({ isValid: false });
        } else if (errors) {
            delete errors[field];
            this.setState({
                errors,
            });
            onValidationUpdate({ isValid: !Object.keys(errors).length });
        } else {
            onValidationUpdate({ isValid: true });
        }
    }
    render() {
        const {
            newCardForm,
            newBillingForm,
            verifyCardForm,
            wrapperClass,
            isVerifying,
            viewer,
            isPaymentSaving,
            isBillingSameAsShipping,
            checkoutPaymentErrors = {},
            buyer,
            shippingCountry,
        } = this.props;
        const showValidationErrors = !!Object.keys(checkoutPaymentErrors).length;
        const maskedCardNumber = this.getSelectedCreditCard()?.maskedCardNumber || '';
        const submitClasses = classNames(
            ctaStyles.defaultButton,
            ctaStyles.defaultWidth,
            ctaStyles.marginTop,
            ctaStyles.form,
            wrapperClass
        );
        const isTrade = buyer?.isTrade;
        const foreignBillingCountry = isBillingSameAsShipping
            ? !!shippingCountry && shippingCountry !== 'US'
            : !!newBillingForm?.country && newBillingForm?.country !== 'US';

        return (
            <Fragment>
                {isVerifying && (
                    <div className={styles.container}>
                        <p className={styles.header}>
                            <FormattedMessage
                                id="checkout.CheckoutCreditCardForm.verifyCardHeader"
                                defaultMessage="Please verify your card"
                            />
                            <span className={styles.digits}>
                                <FormattedMessage
                                    id="checkout.CheckoutCreditCardForm.verifyCardDigits"
                                    defaultMessage="ending in {lastDigits}"
                                    values={{ lastDigits: maskedCardNumber.slice(-4) }}
                                />
                            </span>
                        </p>

                        <p className={styles.largeDisclaimer}>
                            <FormattedMessage
                                id="checkout.CheckoutCreditCardForm.verifyCardText"
                                defaultMessage="For security purposes, we require you to re-enter your credit card number and security code for each new shipping address."
                            />
                        </p>
                    </div>
                )}
                <CreditCardForm
                    creditCard={isVerifying ? verifyCardForm : newCardForm}
                    isVerifying={isVerifying}
                    theme={{ submit: submitClasses }}
                    validations={isVerifying ? { maskedCardNumber } : {}}
                    disabled={
                        isVerifying
                            ? creditCardFields.filter(f => !verifyCardFields.includes(f))
                            : []
                    }
                    hideSubmit
                    disableForm={isPaymentSaving}
                    submitLabel={
                        isVerifying ? <CheckoutContinueAction /> : <CheckoutSaveAndContinueAction />
                    }
                    showAllErrors={showValidationErrors}
                    renderChildren={() => {
                        const showAddressForm = !isBillingSameAsShipping;
                        return (
                            !isVerifying && (
                                <div>
                                    <Checkbox
                                        name="isBillingSameAsShipping"
                                        label={
                                            <FormattedMessage
                                                id="checkout.CheckoutCreditCardForm.sameAsShipping"
                                                defaultMessage="Billing Address is the Same as Shipping Address"
                                            />
                                        }
                                        checked={isBillingSameAsShipping}
                                        wrapperClass={styles.checkbox}
                                        onChange={this.onToggle}
                                        dataTn="same-as-shipping-address-checkbox"
                                    />
                                    {showAddressForm && (
                                        <ControlledAddressForm
                                            relay={this.props.relay}
                                            viewer={viewer}
                                            address={newBillingForm}
                                            hideCompany
                                            hidePhoneNumber
                                            hideSubmit
                                            validations={{ allowPOBox: true }}
                                            disableForm={isPaymentSaving}
                                            onUpdate={this.onChange}
                                            onBlur={this.onBlur}
                                            showAllErrors={showValidationErrors}
                                        />
                                    )}
                                    {isTrade && foreignBillingCountry && (
                                        <CheckoutEoriVatForm buyer={buyer} />
                                    )}
                                </div>
                            )
                        );
                    }}
                    onUpdate={this.onChange}
                    onBlur={this.onBlur}
                    onSubmit={({ onError }) => this.onSubmit({ onError })}
                    fullWidth
                />
            </Fragment>
        );
    }
}

CheckoutCreditCardForm.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    shippingAddressId: PropTypes.string.isRequired,
    creditCardToken: PropTypes.string,
    wrapperClass: PropTypes.string,
    isVerifying: PropTypes.bool.isRequired,
    newCardForm: PropTypes.object.isRequired,
    newBillingForm: PropTypes.object.isRequired,
    verifyCardForm: PropTypes.object.isRequired,
    onCreditCardVaultedError: PropTypes.func.isRequired,
    onCreditCardFormUpdated: PropTypes.func.isRequired,
    isPaymentSaving: PropTypes.bool.isRequired,
    isBillingSameAsShipping: PropTypes.bool.isRequired,
    onToggleBillingAddress: PropTypes.func.isRequired,
    checkoutPaymentErrors: PropTypes.object,
    onValidationUpdate: PropTypes.func,
    onValidationError: PropTypes.func.isRequired,
    shippingCountry: PropTypes.string,
    onTriggeredErrors: PropTypes.func.isRequired,
};

const ConnectedCheckoutCreditCardForm = connect(
    ({ transaction, payment, shipping }) => {
        const isVerifying = isVerifyingCreditCard(payment);
        return {
            shippingAddressId: transaction.shippingAddressId,
            shippingCountry: shipping.defaultCountry,
            newCardForm: payment.newCardForm,
            newBillingForm: payment.newBillingForm,
            verifyCardForm: payment.verifyCardForm,
            creditCardToken: isVerifying ? payment.verifyCardToken : payment.creditCardToken,
            isPaymentSaving: payment.isPaymentSaving,
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            isVerifying,
        };
    },
    {
        onCreditCardVaultedError: creditCardVaultedError,
        onCreditCardFormUpdated: creditCardFormUpdated,
        onToggleBillingAddress: toggleBillingAddress,
        onTriggeredErrors: setTriggeredErrors,
    }
)(CheckoutCreditCardForm);

export default createFragmentContainer(ConnectedCheckoutCreditCardForm, {
    viewer: graphql`
        fragment CheckoutCreditCardForm_viewer on Viewer {
            ...ControlledAddressForm_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutCreditCardForm_buyer on User {
            ...CheckoutEoriVatForm_buyer
            isTrade: isVerifiedTrade
            wallet {
                paymentMethods {
                    paymentMethod {
                        ... on CreditCard {
                            token
                            maskedCardNumber
                        }
                    }
                }
            }
        }
    `,
});
